import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { FileText, Clock, CheckCircle2, XCircle, Upload, ArrowLeft, Eye, Send, AlertTriangle } from 'lucide-react';
import { PageHeader } from '../components/common/PageHeader';
import { DataCard } from '../components/common/DataCard';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from '../components/ui/dialog';
import {
  Tabs, TabsList, TabsTrigger,
} from '../components/ui/tabs';
import { useApp } from '../contexts/AppContext';
import { toast } from 'sonner';

// Mock assigned assignments for demo
const MOCK_ASSIGNMENTS = [
  { _id: 'sa_1', title: 'Chapter 5: Quadratic Equations', type: 'Assignment', subject: 'Mathematics', dueDate: '2026-04-15', status: 'pending', classId: '10', sectionId: 'A', marks: 20, description: 'Solve exercises 5.1 to 5.3 from NCERT Mathematics Class 10.' },
  { _id: 'sa_2', title: 'Unit Test - Periodic Table', type: 'Test', subject: 'Chemistry', dueDate: '2026-04-12', status: 'pending', classId: '10', sectionId: 'A', marks: 50, maxMarks: 50, questions: [{ text: 'Define atomic number', type: 'Short Answer', marks: '5' }, { text: 'Explain periodicity', type: 'Long Answer', marks: '10' }] },
  { _id: 'sa_3', title: 'Essay: Environmental Conservation', type: 'Homework', subject: 'English', dueDate: '2026-04-10', status: 'submitted', classId: '10', sectionId: 'A', marks: 15, submittedAt: '2026-04-09T10:30:00Z' },
  { _id: 'sa_4', title: 'Debate: AI in Education', type: 'Debate', subject: 'Computer Science', dueDate: '2026-04-20', status: 'pending', classId: '10', sectionId: 'A', description: 'Prepare arguments for/against AI in classrooms.' },
  { _id: 'sa_5', title: 'Book Summary: The Guide', type: 'Summary', subject: 'English', dueDate: '2026-04-08', status: 'evaluated', classId: '10', sectionId: 'A', marks: 10, score: 8, feedback: 'Good summary but missing key themes from chapter 3.' },
];

const AI_STATUS = { plagiarism: 'Pending', similarity: '--', accuracy: 'Pending', aiScore: '--', feedback: 'Pending' };

export default function StudentAssignmentsPage() {
  const { currentUser } = useApp();
  const navigate = useNavigate();
  const [assignments] = useState(MOCK_ASSIGNMENTS);
  const [tab, setTab] = useState('all');
  const [selectedAssignment, setSelectedAssignment] = useState(null);
  const [submitDialog, setSubmitDialog] = useState(null);
  const [submitFile, setSubmitFile] = useState(null);
  const [submitText, setSubmitText] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submissions, setSubmissions] = useState({});

  const filtered = assignments.filter(a => {
    if (tab === 'pending') return a.status === 'pending';
    if (tab === 'submitted') return a.status === 'submitted' || submissions[a._id];
    if (tab === 'evaluated') return a.status === 'evaluated';
    return true;
  });

  const handleSubmit = (aId) => {
    setIsSubmitting(true);
    setTimeout(() => {
      setSubmissions(prev => ({ ...prev, [aId]: { submittedAt: new Date().toISOString(), file: submitFile?.name || null, text: submitText } }));
      toast.success('Work submitted successfully');
      setSubmitDialog(null);
      setSubmitFile(null);
      setSubmitText('');
      setIsSubmitting(false);
    }, 1200);
  };

  const getStatusBadge = (a) => {
    if (submissions[a._id]) return <Badge className="bg-blue-500/10 text-blue-600">Submitted</Badge>;
    if (a.status === 'evaluated') return <Badge className="bg-emerald-500/10 text-emerald-600">Evaluated</Badge>;
    if (a.status === 'submitted') return <Badge className="bg-blue-500/10 text-blue-600">Submitted</Badge>;
    const due = new Date(a.dueDate);
    const now = new Date();
    if (due < now) return <Badge className="bg-red-500/10 text-red-600">Overdue</Badge>;
    return <Badge className="bg-amber-500/10 text-amber-600">Pending</Badge>;
  };

  // AI Evaluation Panel
  const renderAIPanel = (a) => (
    <div className="border border-dashed border-slate-300 dark:border-slate-600 rounded-xl p-4 space-y-2 bg-slate-50/50 dark:bg-slate-800/30" data-testid="ai-evaluation-panel">
      <p className="text-xs font-semibold text-muted-foreground">AI Evaluation Panel</p>
      <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
        {[
          { label: 'Plagiarism', value: a.status === 'evaluated' ? 'Clear' : AI_STATUS.plagiarism, status: a.status === 'evaluated' ? 'Completed' : 'Not Checked' },
          { label: 'Similarity %', value: a.status === 'evaluated' ? '5%' : AI_STATUS.similarity, status: a.status === 'evaluated' ? 'Completed' : 'Not Checked' },
          { label: 'Answer Accuracy', value: a.status === 'evaluated' ? '85%' : AI_STATUS.accuracy, status: a.status === 'evaluated' ? 'Completed' : 'Not Checked' },
          { label: 'AI Score', value: a.status === 'evaluated' ? `${a.score}/${a.marks}` : AI_STATUS.aiScore, status: a.status === 'evaluated' ? 'Completed' : 'Not Checked' },
          { label: 'Feedback', value: a.status === 'evaluated' ? 'Available' : AI_STATUS.feedback, status: a.status === 'evaluated' ? 'Completed' : 'Not Checked' },
        ].map(item => (
          <div key={item.label} className="text-center p-2 rounded-lg bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700">
            <p className="text-[10px] text-muted-foreground">{item.label}</p>
            <p className="text-sm font-medium mt-0.5">{item.value}</p>
            <Badge variant="outline" className="text-[9px] mt-1">{item.status}</Badge>
          </div>
        ))}
      </div>
    </div>
  );

  return (
    <div data-testid="student-assignments-page" className="space-y-6 animate-fade-in">
      <div className="flex items-center gap-3">
        <Button variant="ghost" size="sm" onClick={() => navigate('/learning')} data-testid="back-btn"><ArrowLeft className="w-4 h-4" /></Button>
        <PageHeader title="My Assignments" description="View and submit your assignments" />
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList>
          <TabsTrigger value="all" data-testid="tab-all">All ({assignments.length})</TabsTrigger>
          <TabsTrigger value="pending" data-testid="tab-pending">Pending ({assignments.filter(a => a.status === 'pending' && !submissions[a._id]).length})</TabsTrigger>
          <TabsTrigger value="submitted" data-testid="tab-submitted">Submitted ({assignments.filter(a => a.status === 'submitted' || submissions[a._id]).length})</TabsTrigger>
          <TabsTrigger value="evaluated" data-testid="tab-evaluated">Evaluated ({assignments.filter(a => a.status === 'evaluated').length})</TabsTrigger>
        </TabsList>
      </Tabs>

      <div className="space-y-3">
        {filtered.length === 0 ? (
          <DataCard className="text-center py-12">
            <FileText className="w-12 h-12 mx-auto text-muted-foreground mb-3" />
            <p className="text-muted-foreground text-sm">No assignments in this category.</p>
          </DataCard>
        ) : (
          filtered.map(a => (
            <div key={a._id} className="flex items-center justify-between p-4 border border-slate-200 dark:border-slate-700 rounded-xl hover:shadow-sm transition-shadow" data-testid={`assignment-${a._id}`}>
              <div className="flex-1 min-w-0 cursor-pointer" onClick={() => setSelectedAssignment(a)}>
                <div className="flex items-center gap-2 flex-wrap">
                  <p className="text-sm font-medium truncate">{a.title}</p>
                  <Badge variant="outline" className="text-[10px]">{a.type}</Badge>
                  {getStatusBadge(a)}
                </div>
                <p className="text-xs text-muted-foreground mt-0.5">
                  {a.subject} · Due: {a.dueDate}
                  {a.marks && ` · ${a.marks} marks`}
                  {a.status === 'evaluated' && a.score !== undefined && ` · Score: ${a.score}/${a.marks}`}
                </p>
              </div>
              <div className="flex items-center gap-1 shrink-0 ml-3">
                <Button variant="ghost" size="sm" onClick={() => setSelectedAssignment(a)} data-testid={`view-${a._id}`}><Eye className="w-4 h-4" /></Button>
                {a.status === 'pending' && !submissions[a._id] && (
                  <Button size="sm" variant="outline" onClick={() => setSubmitDialog(a)} data-testid={`submit-${a._id}`}><Upload className="w-3.5 h-3.5 mr-1" /> Submit</Button>
                )}
              </div>
            </div>
          ))
        )}
      </div>

      {/* View Assignment Dialog */}
      <Dialog open={!!selectedAssignment} onOpenChange={() => setSelectedAssignment(null)}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
          {selectedAssignment && (
            <>
              <DialogHeader>
                <DialogTitle>{selectedAssignment.title}</DialogTitle>
                <DialogDescription>{selectedAssignment.type} · {selectedAssignment.subject} · Due: {selectedAssignment.dueDate}</DialogDescription>
              </DialogHeader>
              <div className="space-y-3 text-sm">
                {selectedAssignment.description && <div><p className="text-muted-foreground text-xs">Description</p><p>{selectedAssignment.description}</p></div>}
                {selectedAssignment.marks && <div><p className="text-muted-foreground text-xs">Marks</p><p>{selectedAssignment.marks}</p></div>}
                {selectedAssignment.questions?.length > 0 && (
                  <div>
                    <p className="text-muted-foreground text-xs mb-2">Questions</p>
                    {selectedAssignment.questions.map((q, i) => (
                      <div key={i} className="p-2 border rounded-lg mb-2">
                        <p className="font-medium">Q{i + 1}. {q.text} <Badge variant="outline" className="text-[9px] ml-1">{q.type}</Badge> <span className="text-muted-foreground text-xs">({q.marks} marks)</span></p>
                      </div>
                    ))}
                  </div>
                )}
                {selectedAssignment.status === 'evaluated' && selectedAssignment.feedback && (
                  <div className="p-3 bg-emerald-50 dark:bg-emerald-900/10 rounded-xl border border-emerald-200 dark:border-emerald-800">
                    <p className="text-xs font-medium text-emerald-700 dark:text-emerald-400 mb-1">Teacher Feedback</p>
                    <p className="text-sm">{selectedAssignment.feedback}</p>
                  </div>
                )}
                {submissions[selectedAssignment._id] && (
                  <div className="p-3 bg-blue-50 dark:bg-blue-900/10 rounded-xl border border-blue-200 dark:border-blue-800">
                    <p className="text-xs font-medium text-blue-700 dark:text-blue-400 mb-1">Your Submission</p>
                    <p className="text-xs text-muted-foreground">Submitted at: {new Date(submissions[selectedAssignment._id].submittedAt).toLocaleString()}</p>
                    {submissions[selectedAssignment._id].file && <p className="text-xs">File: {submissions[selectedAssignment._id].file}</p>}
                    {submissions[selectedAssignment._id].text && <p className="text-sm mt-1">{submissions[selectedAssignment._id].text}</p>}
                  </div>
                )}
              </div>
              {renderAIPanel(selectedAssignment)}
              {selectedAssignment.status === 'pending' && !submissions[selectedAssignment._id] && (
                <div className="flex justify-end">
                  <Button onClick={() => { setSelectedAssignment(null); setSubmitDialog(selectedAssignment); }} className="bg-brand-deep-blue" data-testid="submit-from-view">
                    <Upload className="w-4 h-4 mr-1" /> Submit Work
                  </Button>
                </div>
              )}
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Submit Dialog */}
      <Dialog open={!!submitDialog} onOpenChange={() => { setSubmitDialog(null); setSubmitFile(null); setSubmitText(''); }}>
        <DialogContent className="max-w-md">
          {submitDialog && (
            <>
              <DialogHeader>
                <DialogTitle>Submit: {submitDialog.title}</DialogTitle>
                <DialogDescription>Upload your work or type your answer</DialogDescription>
              </DialogHeader>
              <div className="space-y-4">
                <div>
                  <p className="text-xs font-medium mb-1">Upload File</p>
                  <Input type="file" onChange={e => setSubmitFile(e.target.files?.[0] || null)} data-testid="submit-file" />
                  <p className="text-[10px] text-muted-foreground mt-1">PDF, DOC, images accepted (UI only)</p>
                </div>
                <div>
                  <p className="text-xs font-medium mb-1">Or type your answer</p>
                  <Textarea value={submitText} onChange={e => setSubmitText(e.target.value)} rows={4} placeholder="Write your answer here..." data-testid="submit-text" />
                </div>
                <div className="flex justify-end gap-2">
                  <Button variant="outline" onClick={() => setSubmitDialog(null)}>Cancel</Button>
                  <Button onClick={() => handleSubmit(submitDialog._id)} disabled={isSubmitting || (!submitFile && !submitText)} className="bg-brand-deep-blue" data-testid="confirm-submit-btn">
                    {isSubmitting ? (
                      <><div className="w-3.5 h-3.5 border-2 border-white border-t-transparent rounded-full animate-spin mr-1" /> Submitting...</>
                    ) : (
                      <><Send className="w-3.5 h-3.5 mr-1" /> Submit</>
                    )}
                  </Button>
                </div>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
